import FullLayout from 'layout/FullLayout';
import { useState, useEffect, useRef } from 'react';
import { Formik, FieldArray } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import qs from 'qs';
import { useQuery } from '@tanstack/react-query';
import {
  IconBrandWhatsapp,
  IconBuildingBank,
  IconChevronDown,
  IconChevronUp,
  IconWallet,
} from '@tabler/icons-react';
import { CircularProgress } from '@mui/material';

import InputText from 'components/InputText';
import InputSelected from 'components/InputSelected';
import { openAuthModalEvent } from 'context/AuthModalEvent';
import CartSummary from './ComponentsPage/CartSummary';
import { API_URL, get } from 'lib/network/api';
import { sanitizeParticipants } from 'lib/utils';
import SelectedParticipant from 'pages/Class/ComponentsPage/SelectedParticipant';
import {
  useCreateInvoiceMutation,
  useGetBalanceData,
  useInvoiceStatusQuery,
} from 'pages/Checkout/hook/use-checkout';

const splitName = (fullName) => {
  if (!fullName) return { first_name: '', last_name: '' };
  const parts = fullName.trim().split(' ');
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
};

const OptionParticipant = [
  { id: 0, name: 'Adult', value: 'adult' },
  { id: 1, name: 'Child', value: 'child' },
];

const paymentMethodSections = [
  {
    key: 'virtual_account',
    title: 'VIRTUAL ACCOUNT',
    icon: IconBuildingBank,
    options: [
      { key: 'BNI_VA', label: 'BNI', payment_type: 'doitpay_va', logo: '/bank/bni-logo.webp' },
      {
        key: 'MANDIRI_VA',
        label: 'MANDIRI',
        payment_type: 'doitpay_va',
        logo: '/bank/mandiri-logo.webp',
      },
      {
        key: 'BRI_VA',
        label: 'BANK BRI',
        payment_type: 'doitpay_va',
        logo: '/bank/bri-logo.webp',
      },
      {
        key: 'PERMATA_VA',
        label: 'PermataBank',
        payment_type: 'doitpay_va',
        logo: '/bank/permata-logo.webp',
      },
      {
        key: 'CIMB_VA',
        label: 'CIMB',
        payment_type: 'doitpay_va',
        logo: '/bank/cimb.png',
      },
      { key: 'BSI_VA', label: 'BSI', payment_type: 'doitpay_va', logo: '/bank/bsi-logo.webp' },
      {
        key: 'SINARMAS_VA',
        label: 'bank sinarmas',
        payment_type: 'doitpay_va',
        logo: '/bank/sinarmas-logo.webp',
      },
    ],
  },
  {
    key: 'e_wallet',
    title: 'E-WALLET',
    icon: IconWallet,
    options: [
      {
        key: 'DANA',
        label: 'DANA',
        payment_type: 'doitpay',
        logo: '/bank/dana-logo.webp',
      },
    ],
  },
];

const normalizeText = (value) =>
  String(value || '')
    .replace(/\s+/g, '')
    .toUpperCase();

const getInvoicePayload = (response) => {
  if (!response) return null;

  if (response?.data?.invoice_number) return response.data;
  if (response?.invoice_number) return response;

  return null;
};

const getBankLogoFromPaymentMethods = (bankName) => {
  const normalizedBankName = normalizeText(bankName);
  if (!normalizedBankName) return null;

  const virtualAccountSection = paymentMethodSections.find(
    (section) => section.key === 'virtual_account',
  );
  const options = virtualAccountSection?.options || [];

  const matchedOption = options.find((option) => {
    const normalizedLabel = normalizeText(option.label).replace('BANK', '');
    return (
      normalizedLabel.includes(normalizedBankName) || normalizedBankName.includes(normalizedLabel)
    );
  });

  return matchedOption?.logo || null;
};

const validationSchema = Yup.object({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string(),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone_number: Yup.string().required('Phone number is required'),
  payment_type: Yup.string().required('Please choose a payment method'),
  payment_method_option: Yup.string().required('Please choose a payment method option'),
  participant_ids: Yup.array().when('$hasSchedule', {
    is: true,
    then: (schema) => schema.min(1, 'Please select at least one participant or add a new one'),
    otherwise: (schema) => schema.notRequired(),
  }),

  new_participants: Yup.array().notRequired(),
});

const fetchPackageDetail = async ({ queryKey }) => {
  const [_key, { package_id }] = queryKey;
  const endpoint = `${API_URL}/api/event/package/${package_id}/no-login`;
  return await get(endpoint);
};

const fetchDetailSchedule = async ({ queryKey }) => {
  const [_key, { schedule_id }] = queryKey;
  const endpoint = `${API_URL}/api/event/schedule/${schedule_id}/no-login?data_type=class_schedule`;
  return await get(endpoint);
};

const fetchDetailClass = async ({ queryKey }) => {
  const [_key, { class_id }] = queryKey;
  const endpoint = `${API_URL}/api/event/class/${class_id}`;
  return await get(endpoint);
};

const formatCurrencyIDR = (amount) => {
  const numericAmount = Number(amount || 0);
  return `Rp ${numericAmount.toLocaleString('id-ID')}`;
};

const formatBalanceAmount = (value, currency = 'IDR') => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount)) return '-';

  if (currency === 'IDR') {
    return `Rp ${amount.toLocaleString('id-ID')}`;
  }

  return `${currency} ${amount.toLocaleString('en-US')}`;
};

const getStatusBadgeClasses = (status) => {
  if (status === 'PAID' || status === 'SUCCESS') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }

  if (status === 'ACTIVE') {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }

  if (status === 'EXPIRED' || status === 'FAILED' || status === 'CANCELLED') {
    return 'bg-red-50 text-red-700 border-red-200';
  }

  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const formatDateTimeID = (value) => {
  if (!value) return '-';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const InfoRow = ({ label, value, className = '' }) => (
  <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p
      className={`break-all text-sm font-semibold text-gray-900 sm:max-w-[70%] sm:text-right ${className}`}>
      {value || '-'}
    </p>
  </div>
);

const InvoiceStatusNotice = ({ invoiceStatus }) => {
  if (invoiceStatus === 'EXPIRED') {
    return (
      <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
        <p className="text-sm font-semibold text-red-900">Invoice sudah kedaluwarsa</p>
        <p className="mt-1 text-sm text-red-800">
          Silakan buat invoice baru untuk melanjutkan pembayaran.
        </p>
      </div>
    );
  }

  if (invoiceStatus === 'FAILED') {
    return (
      <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
        <p className="text-sm font-semibold text-red-900">Pembayaran gagal</p>
        <p className="mt-1 text-sm text-red-800">
          Silakan coba lagi dengan membuat invoice baru atau pilih metode pembayaran lain.
        </p>
      </div>
    );
  }

  return null;
};

const fetchUserProfile = async () => {
  const response = await get(`${API_URL}/api/user`);
  if (!response || response.error) {
    throw new Error(response?.message || 'Failed to fetch user data');
  }
  return response;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const hasOpenedAuthModalRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    mutate: createInvoice,
    isPending: isCreatingInvoice,
    data: createdInvoice,
  } = useCreateInvoiceMutation();
  const currentParams = qs.parse(searchParams.toString(), { comma: true });
  const { class_id, schedule_id, package_id, package_type, invoice_number } = currentParams;
  const {
    data,
    isLoading: isLoadingUserProfile,
    isError,
    error,
  } = useQuery({
    queryKey: ['user-profile'],
    queryFn: fetchUserProfile,
    staleTime: 1000 * 60 * 5, // cache 5 menit
  });
  const isDanaBound = Boolean(data?.dana_binding?.is_bound);
  const danaBindingStatusLabel = isDanaBound ? 'Terhubung' : 'Belum Terhubung';
  const { data: danaBalanceData, isFetching: isFetchingDanaBalance } = useGetBalanceData({
    enabled: isDanaBound,
    retry: false,
    staleTime: 1000 * 60,
  });
  const danaBalanceInfo = danaBalanceData?.accountInfos?.find(
    (item) => item?.balanceType === 'BALANCE',
  );
  const danaBalanceAmount = danaBalanceInfo?.amount?.value;
  const danaBalanceCurrency = danaBalanceInfo?.amount?.currency;
  const hasDanaBalanceValue =
    typeof danaBalanceAmount !== 'undefined' && danaBalanceAmount !== null;
  const [quantity, setQuantity] = useState(1);
  const [selectedParticipant, setSelectedParticipant] = useState([]);
  const [openPaymentSection, setOpenPaymentSection] = useState('virtual_account');
  const invoiceNumberFromCreate = createdInvoice?.invoice_number;
  const activeInvoiceNumber = invoice_number || invoiceNumberFromCreate;

  const { data: invoiceStatusResponse, isFetching: isCheckingInvoiceStatus } =
    useInvoiceStatusQuery(activeInvoiceNumber, {
      enabled: Boolean(activeInvoiceNumber),
      refetchInterval: 10000,
    });

  const activeInvoiceData =
    getInvoicePayload(invoiceStatusResponse) || getInvoicePayload(createdInvoice);
  const isResolvingInvoiceFromUrl = Boolean(invoice_number) && !activeInvoiceData;
  const paymentMethodType = activeInvoiceData?.payment_method?.type;
  const paymentMethodFlow = activeInvoiceData?.payment_method?.flow;
  const paymentProvider = activeInvoiceData?.payment_method?.provider;
  const isVirtualAccountInvoice = paymentMethodType === 'virtual_account';
  const isDanaInvoice = paymentProvider === 'DANA';
  const isDanaBindingFlow = isDanaInvoice && paymentMethodFlow === 'binding';
  const isDanaPaymentFlow = isDanaInvoice && paymentMethodFlow === 'payment';
  const danaRedirectUrl =
    activeInvoiceData?.payment_method?.redirect_url || activeInvoiceData?.payment_url || '';
  const shouldShowVirtualAccountResult =
    Boolean(activeInvoiceData?.invoice_number) && isVirtualAccountInvoice;
  const shouldShowDanaResult = Boolean(activeInvoiceData?.invoice_number) && isDanaInvoice;
  const displayedBankName = activeInvoiceData?.payment_method?.bank;
  const displayedAccountNumber = activeInvoiceData?.payment_method?.account_number;
  const displayedBankLogo = getBankLogoFromPaymentMethods(displayedBankName);

  const invoiceStatusRaw =
    activeInvoiceData?.invoice_status ||
    activeInvoiceData?.status ||
    activeInvoiceData?.payment_status;
  const isInvoicePaid =
    Boolean(activeInvoiceData?.is_paid) || String(invoiceStatusRaw || '').toUpperCase() === 'PAID';

  const invoiceStatus = (
    (isInvoicePaid ? 'PAID' : null) ||
    activeInvoiceData?.invoice_status ||
    activeInvoiceData?.status ||
    activeInvoiceData?.payment_status ||
    'PENDING'
  )
    .toString()
    .toUpperCase();

  const isExpiredInvoice = invoiceStatus === 'EXPIRED';
  const isFailedInvoice = invoiceStatus === 'FAILED';
  const isInvoiceInactive = isExpiredInvoice || isFailedInvoice;
  const shouldShowExpiredAt =
    Boolean(activeInvoiceData?.expired_at) &&
    (Boolean(displayedAccountNumber) || isDanaPaymentFlow);
  const shouldShowInvoiceResult = shouldShowVirtualAccountResult || shouldShowDanaResult;

  useEffect(() => {
    if (!invoiceNumberFromCreate) return;
    if (searchParams.get('invoice_number') === invoiceNumberFromCreate) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('invoice_number', invoiceNumberFromCreate);
    setSearchParams(nextParams, { replace: true });
  }, [invoiceNumberFromCreate, searchParams, setSearchParams]);

  useEffect(() => {
    const isPaidByStatus = invoiceStatus === 'PAID' || invoiceStatus === 'SUCCESS';

    if (isInvoicePaid || isPaidByStatus) {
      navigate('/thank-you');
    }
  }, [isInvoicePaid, invoiceStatus, navigate]);

  useEffect(() => {
    if (invoice_number) {
      navigate(`/invoice-detail?invoice_number=${invoice_number}`, { replace: true });
    }
  }, [invoice_number, navigate]);

  useEffect(() => {
    // Only prompt login after profile query has finished and failed.
    if (isLoadingUserProfile) return;
    if (!isError) return;
    if (hasOpenedAuthModalRef.current) return;

    hasOpenedAuthModalRef.current = true;
    sessionStorage.setItem(
      'kin_pending_redirect',
      window.location.pathname + window.location.search,
    );
    openAuthModalEvent('unauthenticated');
  }, [isLoadingUserProfile, isError]);

  const { data: packageData } = useQuery({
    queryKey: ['ticketDetail', { package_id }],
    queryFn: fetchPackageDetail,
    enabled: !!package_id,
  });

  const { data: scheduleDetailData } = useQuery({
    queryKey: ['detailSchedule', { schedule_id }],
    queryFn: fetchDetailSchedule,
    enabled: !!schedule_id,
  });

  const { data: classDetailData } = useQuery({
    queryKey: ['detailClass', { class_id }],
    queryFn: fetchDetailClass,
    enabled: !!class_id,
  });

  const itemPrice = Number(packageData?.price || 0);
  const estimatedSubTotal = itemPrice * quantity;
  // const estimatedTax = Math.round(estimatedSubTotal * 0.1);
  const estimatedTotalToPay = estimatedSubTotal;
  const numericDanaBalance = Number(danaBalanceAmount);
  const hasNumericDanaBalance = Number.isFinite(numericDanaBalance);
  const isDanaBalanceInsufficient =
    isDanaBound && hasNumericDanaBalance && numericDanaBalance < estimatedTotalToPay;

  const nameParts = splitName(data?.name);
  const initialValues = {
    first_name: nameParts.first_name,
    last_name: nameParts.last_name,
    email: data?.email || '',
    phone_number: data?.phone_number || '',
    payment_type: 'doitpay_va',
    payment_method_option: 'BNI_VA',
    participant_ids: [],
    new_participants: [
      {
        name: '',
        type: 'child',
        phone_number: '',
      },
    ],
  };

  const handleSelectedParticipant = (participants, setFieldValue) => {
    setSelectedParticipant(participants);
    setFieldValue(
      'participant_ids',
      participants.map((p) => p.id),
    );
  };

  const goContactAdmin = (formValues) => {
    const phoneNumber = '6281290904616';

    // Prepare message with all available information
    const messageParts = [];

    // User information
    messageParts.push(`*Informasi Pengunjung*`);
    messageParts.push(`Nama: ${formValues?.first_name || ''} ${formValues?.last_name || ''}`);
    messageParts.push(`Email: ${formValues?.email || '-'}`);
    messageParts.push(`WhatsApp: ${formValues?.phone_number || '-'}`);

    // Class information
    if (classDetailData?.data?.class) {
      messageParts.push(`\n*Informasi Class*`);
      messageParts.push(`Class: ${classDetailData.data.class.name || '-'}`);
      messageParts.push(`Subject: ${classDetailData.data.class.subject || '-'}`);
      messageParts.push(`Theme: ${classDetailData.data.class.theme || '-'}`);
    }

    // Schedule information
    if (scheduleDetailData?.data?.schedule) {
      messageParts.push(`\n*Jadwal*`);
      messageParts.push(`Tanggal: ${scheduleDetailData.data.schedule.date || '-'}`);
      messageParts.push(`Waktu: ${scheduleDetailData.data.schedule.time || '-'}`);
      if (scheduleDetailData.data.schedule.location) {
        messageParts.push(`Lokasi: ${scheduleDetailData.data.schedule.location}`);
      }
    }

    // Package information
    if (packageData?.package_name) {
      messageParts.push(`\n*Paket*`);
      messageParts.push(`Paket: ${packageData.package_name}`);
      messageParts.push(`Harga: Rp ${packageData?.price?.toLocaleString('id-ID') || '-'}`);
    }

    messageParts.push(`\n*Pesan*`);
    messageParts.push(
      `Saya tertarik dengan class ini dan ingin mendapatkan informasi lebih lanjut.`,
    );

    const message = encodeURIComponent(messageParts.join('\n'));
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;

    window.open(whatsappUrl, '_blank');
  };

  const handleOnSubmit = async (values) => {
    if (!packageData) {
      toast.error('Ticket data not available');
      return;
    }

    if (values.payment_method_option === 'DANA' && isDanaBalanceInsufficient) {
      toast.error('Saldo DANA tidak cukup untuk total pembayaran ini.');
      return;
    }

    const participantIds = values.participant_ids || [];
    const filteredParticipants = sanitizeParticipants(values.new_participants);

    const invoicePayload = {
      invoice_req: {
        payment_type: values.payment_type,
        schedule_id: schedule_id || '',
        payment_channel: values.payment_method_option,
        exhibition_play_id: '',
        type: package_type,
        date: '',
        items: [
          {
            event_package_id: packageData?.id,
            event_package_name: packageData?.package_name,
            quantity,
            participant_ids: participantIds,
            new_participants: filteredParticipants.length > 0 ? filteredParticipants : [],
          },
        ],
      },
      tax_amount: Math.round(packageData.price * quantity * 0.1),
    };

    if (package_type === 'event_package') {
      delete invoicePayload.invoice_req.schedule_id;
      delete invoicePayload.invoice_req.exhibition_play_id;
      delete invoicePayload.invoice_req.date;
    } else if (package_type === 'class') {
      delete invoicePayload.invoice_req.exhibition_play_id;
      delete invoicePayload.invoice_req.date;
    }

    if (values.payment_method_option === 'DANA') {
      toast(
        isDanaBound
          ? 'Anda akan diarahkan ke halaman pembayaran DANA.'
          : 'Anda akan diarahkan ke aplikasi DANA untuk menghubungkan akun terlebih dahulu.',
        {
          icon: 'ℹ️',
        },
      );
    }

    createInvoice(invoicePayload, {
      onSuccess: (res) => {
        const invoiceNumber = res?.invoice_number || res?.data?.invoice_number;

        if (!invoiceNumber) {
          toast.error('Invoice berhasil dibuat, tetapi nomor invoice tidak ditemukan.');
          return;
        }

        navigate(`/invoice-detail?invoice_number=${invoiceNumber}`);
      },
    });
  };

  const findValueSelected = (value) => OptionParticipant.find((opt) => opt.value === value) || null;

  const handleCopyVirtualAccount = async () => {
    const vaNumber = displayedAccountNumber;
    if (!vaNumber) return;

    try {
      await navigator.clipboard.writeText(vaNumber);
      toast.success('Virtual account number copied');
    } catch {
      toast.error('Failed to copy virtual account number');
    }
  };

  const handleContactSupport = () => {
    const phoneNumber = '6281290904616';
    const message = encodeURIComponent(
      `Halo Admin KIN, saya membutuhkan bantuan terkait pembayaran VA.` +
        `\nInvoice: ${activeInvoiceData?.invoice_number || '-'} ` +
        `\nVA: ${displayedAccountNumber || '-'} ` +
        `\nTotal: ${formatCurrencyIDR(activeInvoiceData?.total)}`,
    );

    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const handleDanaBinding = () => {
    if (!danaRedirectUrl) return;
    window.open(danaRedirectUrl, '_self');
  };

  const lightenColor = (hex, percent) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    const newR = Math.round(r + (255 - r) * percent);
    const newG = Math.round(g + (255 - g) * percent);
    const newB = Math.round(b + (255 - b) * percent);

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB
      .toString(16)
      .padStart(2, '0')}`.toUpperCase();
  };

  return (
    <FullLayout>
      <div className="max-w-7xl mx-auto pt-10 pb-24 px-4 text-kin-text">
        <div className="flex flex-col lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-10 gap-6">
          <div className="w-full">
            <h2 className="text-xl font-semibold">Your Order Summary</h2>
            <p className="text-base">
              All play passes are valid on the date you have purchased it for.
            </p>

            <div className="mt-6">
              {isResolvingInvoiceFromUrl ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex items-center gap-3">
                  <CircularProgress size={20} />
                  <p className="text-sm text-gray-600">Loading your invoice...</p>
                </div>
              ) : shouldShowVirtualAccountResult ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Berhasil Dibuat</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Silakan selesaikan pembayaran menggunakan Virtual Account di bawah ini.
                  </p>

                  <InvoiceStatusNotice invoiceStatus={invoiceStatus} />

                  <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-gray-500">Bank</p>
                      {displayedBankLogo ? (
                        <img
                          src={displayedBankLogo}
                          alt={displayedBankName || 'Bank logo'}
                          className="h-8 w-auto object-contain"
                        />
                      ) : (
                        <p className="text-sm font-semibold text-gray-900">
                          {displayedBankName || '-'}
                        </p>
                      )}
                    </div>

                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-500">Virtual Account Number</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <p className="break-all text-xl font-semibold text-gray-900">
                          {displayedAccountNumber || '-'}
                        </p>
                        <button
                          type="button"
                          onClick={handleCopyVirtualAccount}
                          disabled={!displayedAccountNumber}
                          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100">
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 rounded-lg border border-gray-200 bg-white">
                      <InfoRow label="Invoice Number" value={activeInvoiceData?.invoice_number} />
                      <div className="border-t border-gray-200">
                        <InfoRow
                          label="Total Payment"
                          value={formatCurrencyIDR(activeInvoiceData?.total)}
                          className="text-base"
                        />
                      </div>
                      {shouldShowExpiredAt && (
                        <div className="border-t border-gray-200">
                          <InfoRow
                            label="Berlaku Sampai"
                            value={formatDateTimeID(activeInvoiceData?.expired_at)}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleCopyVirtualAccount}
                      disabled={!displayedAccountNumber || isInvoiceInactive}
                      className="bg-primary-main disabled:bg-gray-300 text-white rounded-[100px] px-4 py-2 text-sm font-medium">
                      Copy VA Number
                    </button>
                  </div>
                </div>
              ) : shouldShowDanaResult ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Berhasil Dibuat</h3>
                  {isDanaBindingFlow && (
                    <p className="mt-1 text-sm text-gray-600">
                      Sebelum bayar, kamu perlu menghubungkan akun DANA terlebih dahulu.
                    </p>
                  )}
                  {isDanaPaymentFlow && (
                    <p className="mt-1 text-sm text-gray-600">
                      Pembayaran DANA sedang diproses. Lanjutkan pembayaran dari aplikasi DANA.
                    </p>
                  )}

                  <InvoiceStatusNotice invoiceStatus={invoiceStatus} />

                  <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    {isDanaBindingFlow && (
                      <>
                        <p className="text-sm font-semibold text-amber-900">
                          Akun DANA Perlu Dihubungkan
                        </p>
                        <p className="mt-1 text-sm text-amber-800">
                          Klik tombol di bawah, lalu lanjutkan proses hubungkan akun di aplikasi
                          DANA.
                        </p>
                      </>
                    )}
                    {isDanaPaymentFlow && (
                      <>
                        <p className="text-sm font-semibold text-amber-900">
                          Lanjutkan Pembayaran DANA
                        </p>
                        <p className="mt-1 text-sm text-amber-800">
                          Klik tombol di bawah untuk kembali ke halaman pembayaran DANA.
                        </p>
                      </>
                    )}

                    <div className="mt-4 rounded-lg border border-amber-200 bg-white">
                      <InfoRow label="Invoice Number" value={activeInvoiceData?.invoice_number} />
                      <div className="border-t border-gray-200">
                        <InfoRow
                          label="Total Payment"
                          value={formatCurrencyIDR(activeInvoiceData?.total)}
                          className="text-base"
                        />
                      </div>
                      {shouldShowExpiredAt && (
                        <div className="border-t border-gray-200">
                          <InfoRow
                            label="Berlaku Sampai"
                            value={formatDateTimeID(activeInvoiceData?.expired_at)}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleDanaBinding}
                      disabled={!danaRedirectUrl || isInvoiceInactive}
                      className="bg-primary-main disabled:bg-gray-300 text-white rounded-[100px] px-4 py-2 text-sm font-medium">
                      {isDanaBindingFlow ? 'Hubungkan ke DANA' : 'Lanjutkan Pembayaran DANA'}
                    </button>
                  </div>
                </div>
              ) : (
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  validationContext={{ hasSchedule: !!schedule_id }}
                  onSubmit={handleOnSubmit}>
                  {({ handleSubmit, values, setFieldValue, errors, touched }) => {
                    const hasFirstName = Boolean(values.first_name?.trim());
                    const hasLastName = Boolean(values.last_name?.trim());
                    const hasEmail = Boolean(values.email?.trim());
                    const hasPhoneNumber = Boolean(values.phone_number?.trim());
                    const isDanaSelected = values.payment_method_option === 'DANA';
                    const isDanaSubmitDisabled = isDanaSelected && isDanaBalanceInsufficient;

                    return (
                      <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                          <h3 className="text-lg font-semibold">Contact Information</h3>
                          <div className="space-y-6 mt-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <InputText
                                name="first_name"
                                type="text"
                                label="FIRST NAME"
                                disabled={hasFirstName}
                              />
                              <InputText
                                name="last_name"
                                type="text"
                                label="LAST NAME"
                                disabled={hasLastName}
                              />
                            </div>
                            <InputText
                              name="email"
                              type="text"
                              label="EMAIL ADDRESS"
                              disabled={hasEmail}
                            />
                            <InputText
                              name="phone_number"
                              type="text"
                              label="WHATSAPP NUMBER"
                              disabled={hasPhoneNumber}
                            />
                          </div>
                        </div>

                        {/* Participants */}
                        {schedule_id && (
                          <div className="space-y-6">
                            <div>
                              <SelectedParticipant
                                value={selectedParticipant}
                                onChange={(val) => handleSelectedParticipant(val, setFieldValue)}
                              />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold">New Participants</h3>
                              <FieldArray name="new_participants">
                                {({ remove }) => (
                                  <div className="mt-2 flex flex-col gap-4">
                                    {values.new_participants.map((_, index) => (
                                      <div
                                        key={index}
                                        className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <InputText
                                          name={`new_participants[${index}].name`}
                                          label="Name"
                                          type="text"
                                        />

                                        <InputSelected
                                          width="w-full"
                                          label="Participant Type"
                                          name={`new_participants[${index}].type`}
                                          dataOption={OptionParticipant}
                                          selected={findValueSelected(
                                            values.new_participants[index].type,
                                          )}
                                          setSelected={(val) =>
                                            setFieldValue(
                                              `new_participants[${index}].type`,
                                              val?.value || '',
                                            )
                                          }
                                        />

                                        {values.new_participants.length > 1 && (
                                          <button
                                            type="button"
                                            onClick={() => remove(index)}
                                            className="text-red-500 text-sm underline">
                                            Remove Participant
                                          </button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </FieldArray>
                            </div>
                          </div>
                        )}

                        <div>
                          <h3 className="text-lg font-semibold">Payment Method</h3>
                          <div className="mt-2 space-y-3">
                            {paymentMethodSections.map((section) => {
                              const isOpen = openPaymentSection === section.key;
                              const SectionIcon = section.icon;

                              return (
                                <div key={section.key}>
                                  <div className="rounded-xl bg-white shadow-sm overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setOpenPaymentSection((prev) =>
                                          prev === section.key ? '' : section.key,
                                        )
                                      }
                                      className="w-full px-4 py-3 flex items-center justify-between text-left">
                                      <span className="flex items-center gap-2 text-[#2C3441]">
                                        <SectionIcon className="w-5 h-5 text-kin-text" />
                                        <span className="block text-sm font-medium leading-6 text-kin-text">
                                          {section.title}
                                        </span>
                                      </span>
                                      {isOpen ? (
                                        <IconChevronUp className="w-5 h-5 text-kin-text" />
                                      ) : (
                                        <IconChevronDown className="w-5 h-5 text-kin-text" />
                                      )}
                                    </button>
                                  </div>

                                  {isOpen && (
                                    <div className="px-0 py-2 space-y-2">
                                      {section.options.map((option) => {
                                        const isSelected =
                                          values.payment_method_option === option.key;
                                        const isDanaOption = option.key === 'DANA';
                                        const isDanaOptionDisabled =
                                          isDanaOption && isDanaBalanceInsufficient;
                                        const selectedRing = lightenColor('#F58136', 0.55);

                                        return (
                                          <label
                                            key={option.key}
                                            className={`flex items-center justify-between rounded-xl px-5 py-4 cursor-pointer border transition-all duration-150 ${
                                              isDanaOptionDisabled
                                                ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                                                : ''
                                            } ${
                                              isSelected
                                                ? 'border-primary-main'
                                                : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                            style={{
                                              backgroundColor: isDanaOptionDisabled
                                                ? '#F3F4F6'
                                                : '#FFFFFF',
                                              boxShadow: isSelected
                                                ? `0 0 0 1px ${selectedRing}`
                                                : 'none',
                                            }}>
                                            <input
                                              type="radio"
                                              name="payment_method_option"
                                              value={option.key}
                                              checked={isSelected}
                                              disabled={isDanaOptionDisabled}
                                              onChange={() => {
                                                if (isDanaOptionDisabled) return;
                                                setFieldValue('payment_method_option', option.key);
                                                setFieldValue('payment_type', option.payment_type);
                                              }}
                                              className="sr-only"
                                            />

                                            <span className="flex items-center gap-3 min-w-0">
                                              {option.logo ? (
                                                <img
                                                  src={option.logo}
                                                  alt={option.label}
                                                  className="h-6 w-auto object-contain"
                                                />
                                              ) : (
                                                <span
                                                  className={`text-xs font-semibold tracking-tight leading-none uppercase ${
                                                    isSelected
                                                      ? 'text-primary-main'
                                                      : 'text-[#1D4A8D]'
                                                  }`}>
                                                  {option.label}
                                                </span>
                                              )}

                                              {isDanaOption && (
                                                <div className="flex items-center gap-1.5">
                                                  <span
                                                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                                      isDanaBound
                                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                                                    }`}>
                                                    {danaBindingStatusLabel}
                                                  </span>
                                                  {isDanaOptionDisabled && (
                                                    <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                                                      Saldo kurang
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                            </span>

                                            <span
                                              className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                                                isSelected
                                                  ? 'border-primary-main'
                                                  : 'border-gray-300 bg-transparent'
                                              }`}>
                                              {isSelected ? (
                                                <span className="h-3 w-3 rounded-full bg-primary-main" />
                                              ) : null}
                                            </span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {((touched.payment_method_option && errors.payment_method_option) ||
                            (touched.payment_type && errors.payment_type)) && (
                            <p className="mt-2 text-sm text-red-600">
                              {errors.payment_method_option || errors.payment_type}
                            </p>
                          )}

                          {isDanaSelected && (
                            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                              <p className="text-sm font-semibold text-amber-900">
                                {isDanaBound
                                  ? 'Akun DANA sudah terhubung'
                                  : 'DANA perlu dihubungkan terlebih dahulu'}
                              </p>
                              <p className="mt-1 text-sm text-amber-800">
                                {isDanaBound
                                  ? 'Saldo akun akan ditampilkan otomatis. Klik tombol di bawah untuk lanjut ke pembayaran DANA.'
                                  : 'Setelah klik tombol di bawah, sistem akan mengarahkan kamu ke aplikasi DANA untuk menghubungkan akun.'}
                              </p>

                              {isDanaBound && (
                                <div className="mt-3 rounded-lg border border-amber-200 bg-white px-3 py-2">
                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                    Saldo DANA
                                  </p>
                                  <p className="mt-1 text-base font-semibold text-gray-900">
                                    {isFetchingDanaBalance
                                      ? 'Mengambil saldo...'
                                      : hasDanaBalanceValue
                                      ? formatBalanceAmount(
                                          danaBalanceAmount,
                                          danaBalanceCurrency || 'IDR',
                                        )
                                      : '-'}
                                  </p>

                                  {isDanaBalanceInsufficient && (
                                    <p className="mt-1 text-xs font-medium text-red-600">
                                      Saldo DANA kurang dari total pembayaran.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={isCreatingInvoice || isDanaSubmitDisabled}
                          className={`${
                            isCreatingInvoice || isDanaSubmitDisabled
                              ? 'bg-primary-dark disabled:bg-gray-300'
                              : 'bg-primary-main'
                          } text-white rounded-[100px] max-w py-2 px-3 flex justify-center items-center w-[200px]`}>
                          {isCreatingInvoice ? (
                            <CircularProgress style={{ color: '#ffffff' }} size={22} />
                          ) : isDanaSelected ? (
                            isDanaSubmitDisabled ? (
                              'SALDO DANA TIDAK CUKUP'
                            ) : isDanaBound ? (
                              'LANJUT BAYAR DENGAN DANA'
                            ) : (
                              'HUBUNGKAN DANA'
                            )
                          ) : (
                            'CONFIRM & PAY'
                          )}
                        </button>
                      </form>
                    );
                  }}
                </Formik>
              )}
            </div>
          </div>

          {shouldShowInvoiceResult ? (
            <div className="rounded-2xl bg-white border border-gray-200 flex flex-col gap-4 p-5 text-kin-text mt-6 lg:mt-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Payment Guide</h3>
                <p className="mt-1 text-sm text-gray-600">
                  {isExpiredInvoice
                    ? 'Invoice sudah kedaluwarsa. Buat invoice baru untuk melanjutkan pembayaran.'
                    : isFailedInvoice
                    ? 'Pembayaran gagal. Silakan coba kembali dengan invoice baru.'
                    : shouldShowDanaResult
                    ? 'Ikuti proses di DANA sampai pembayaran selesai, lalu kembali ke halaman ini.'
                    : 'Selesaikan pembayaran Virtual Account sebelum melewati batas waktu.'}
                </p>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-sm font-semibold text-gray-900">Payment Status</p>
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClasses(
                      invoiceStatus,
                    )}`}>
                    {invoiceStatus}
                  </span>
                  {isCheckingInvoiceStatus && (
                    <span className="text-xs text-gray-500">Checking latest status...</span>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-900">Quick Summary</p>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-500">Invoice</span>
                    <span className="text-right font-medium text-gray-800 break-all">
                      {activeInvoiceData?.invoice_number || '-'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-gray-500">Total</span>
                    <span className="text-right font-semibold text-gray-900">
                      {formatCurrencyIDR(activeInvoiceData?.total)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-4">
                <p className="text-sm font-semibold text-gray-900">Cara Pembayaran</p>
                <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-gray-700">
                  {isExpiredInvoice || isFailedInvoice ? (
                    <>
                      <li>Periksa status invoice pada bagian Payment Status.</li>
                      <li>Buat invoice baru dari halaman checkout.</li>
                      <li>
                        Pastikan metode pembayaran dan data peserta sudah benar sebelum submit
                        ulang.
                      </li>
                    </>
                  ) : shouldShowDanaResult ? (
                    <>
                      <li>Klik tombol aksi DANA untuk membuka aplikasi/halaman pembayaran DANA.</li>
                      <li>
                        Login atau konfirmasi akun DANA kamu, lalu selesaikan proses pembayaran.
                      </li>
                      <li>Kembali ke halaman ini untuk melihat status pembayaran terbaru.</li>
                    </>
                  ) : (
                    <>
                      <li>
                        Buka mobile banking atau ATM yang mendukung pembayaran Virtual Account.
                      </li>
                      <li>Pilih menu pembayaran Virtual Account.</li>
                      <li>Masukkan nomor VA dan konfirmasi nominal pembayaran.</li>
                      <li>Selesaikan transaksi lalu simpan bukti pembayaran.</li>
                    </>
                  )}
                </ol>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleContactSupport}
                  className="bg-[#25D366] text-white rounded-[100px] py-2 px-4 flex items-center gap-1.5 text-sm font-medium hover:bg-[#20BA5A]">
                  <IconBrandWhatsapp className="w-4 h-4" />
                  Need Help?
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-white flex flex-col divide-y-[0.5px] divide-kin-text text-kin-text mt-6 lg:mt-0">
              <CartSummary
                scheduleData={scheduleDetailData}
                classData={classDetailData}
                item={packageData}
                quantity={quantity}
                setQuantity={setQuantity}
              />
            </div>
          )}
        </div>
      </div>
    </FullLayout>
  );
};

export default CheckoutPage;
